/**
 * "Invite team": sits to the LEFT of Settings on the project page's breadcrumb
 * row (IssuesPage.tsx) and opens InviteTeamModal in place, so inviting never
 * leaves the project. (For a few hours on 2026-09-18 it was a link to the
 * Account page's form; Emily: "add an invite modal, and of course always add
 * click events".)
 *
 * The gate mirrors the server's, so nobody is handed a control that refuses:
 *   - owner or admin only. TeamMembersSection renders its form on exactly
 *     `callerRole === 'owner' || callerRole === 'admin'`.
 *   - the viewer's ACTIVE team must own this project. The modal invites into
 *     the team it is handed; on a project shared in from another team that
 *     would be the wrong one, and the viewer has no standing in the right one.
 *
 * Analytics: `invite_team_clicked { source: 'project_header' }`, once per press
 * (a human gesture; never on render). The dashboard drops $autocapture in
 * before_send (lib/posthogConfig.ts), so without it this door is invisible.
 *
 * The `?invite=1` link that opened this modal from team review's onboarding
 * card (2026-09-30) was removed on 2026-10-21, after cb337d7a6 replaced that ask
 * with "Create client link"; it had 0 uses.
 */
import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserAdd01Icon } from '@hugeicons/core-free-icons';
import { useAppSession } from '../../hooks/useAppSession';
import { capture } from '@/lib/analytics';
import { InviteTeamModal } from './InviteTeamModal';

interface InviteTeamButtonProps {
  /** The team that owns the project on screen (`project.teamId`). */
  projectTeamId: string | undefined;
}

export function InviteTeamButton({ projectTeamId }: InviteTeamButtonProps) {
  const { data: session } = useAppSession();
  const [open, setOpen] = useState(false);
  const team = session?.team;
  const canInvite = team?.role === 'owner' || team?.role === 'admin';
  const allowed = !!team && canInvite && !!projectTeamId && team.id === projectTeamId;

  if (!allowed) return null;

  const handleOpen = () => {
    capture('invite_team_clicked', { source: 'project_header' });
    setOpen(true);
  };

  // Same quiet SHAPE as the Settings button beside it (IssuesPage.tsx), in brand
  // blue: Emily, 2026-09-22, "make invite team a blue version". The colour marks
  // it as the action of the pair; Settings stays gray-500. primary-600 measures
  // 6.42:1 on white and 6.09:1 on the gray-50 page, over the 4.5:1 AA floor.
  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors"
        onClick={handleOpen}
        aria-haspopup="dialog"
      >
        <HugeiconsIcon icon={UserAdd01Icon} size={18} strokeWidth={2} color="currentColor" className="w-[18px] h-[18px]" aria-hidden="true" />
        <span className="text-cap-trim">Invite team</span>
      </button>
      {open && <InviteTeamModal teamId={projectTeamId} onClose={() => setOpen(false)} />}
    </>
  );
}
